import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Globe,
    Lock,
    ShoppingBag,
    CheckCircle2,
    Clock,
    DollarSign,
    Layers,
    Tag,
    LayoutGrid,
    List,
    X,
    Save,
    AlertCircle,
    Loader2,
    Image as ImageIcon
} from "lucide-react";
import { useArtistMerch } from "../../services/admin/ArtistMerchServices";
import ViewArtistMerchModal from "../../components/admin/ViewArtistMerchModal";
import { useNotification } from "../../context/NotificationContext";
import { Eye } from "lucide-react";

export default function ArtistMerchPage() {
    const { showNotification } = useNotification();
    const {
        loading,
        fetchMerchCategories,
        fetchAllMerch,
        createMerchProduct,
        updateMerchProduct,
        deleteMerchProduct,
        toggleMerchStatus
    } = useArtistMerch();

    const [merch, setMerch] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("grid"); // grid or list
    const [statusLoading, setStatusLoading] = useState({});

    // Modal States
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewProductId, setViewProductId] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, product: null });
    const [formData, setFormData] = useState({
        name: "",
        category_id: "",
        price: "",
        stock_quantity: "",
        description: "",
        status: "active",
        is_popular: false,
        image_file: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const loadData = useCallback(async () => {
        const [merchData, catData] = await Promise.all([
            fetchAllMerch(),
            fetchMerchCategories()
        ]);
        setMerch(Array.isArray(merchData) ? merchData : []);
        setCategories(Array.isArray(catData) ? catData : []);
    }, [fetchAllMerch, fetchMerchCategories]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                name: product.name,
                category_id: product.category_id,
                price: product.price,
                stock_quantity: product.stock_quantity,
                description: product.description || "",
                status: product.status,
                is_popular: product.is_popular || false,
                image_file: null
            });
            setImagePreview(product.image);
        } else {
            setEditingProduct(null);
            setFormData({
                name: "",
                category_id: "",
                price: "",
                stock_quantity: "",
                description: "",
                status: "active",
                is_popular: false,
                image_file: null
            });
            setImagePreview(null);
        }
        setIsFormModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsFormModalOpen(false);
        setEditingProduct(null);
        setImagePreview(null);
        setFormData({
            name: "",
            category_id: "",
            price: "",
            stock_quantity: "",
            description: "",
            status: "active",
            is_popular: false,
            image_file: null
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image_file: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Frontend Validation
        if (!formData.name || !formData.category_id || !formData.price || !formData.stock_quantity || !formData.description) {
            showNotification({ type: "failed", title: "Validation Error", message: "All fields except 'Popular' are required." });
            return;
        }

        if (!editingProduct && !formData.image_file) {
            showNotification({ type: "failed", title: "Validation Error", message: "Please upload a product image." });
            return;
        }

        setIsSubmitting(true);
        try {
            let res;
            if (editingProduct) {
                res = await updateMerchProduct(editingProduct.id, formData);
            } else {
                res = await createMerchProduct(formData);
            }

            if (res.status === "success") {
                handleCloseModal();
                loadData();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleStatus = async (id) => {
        setStatusLoading(prev => ({ ...prev, [id]: true }));
        const res = await toggleMerchStatus(id);
        if (res.status === "success") {
            await loadData();
        }
        setStatusLoading(prev => ({ ...prev, [id]: false }));
    };

    const handleDeleteClick = (product) => {
        setDeleteModal({ isOpen: true, product });
    };

    const handleViewClick = (id) => {
        setViewProductId(id);
        setIsViewModalOpen(true);
    };

    const confirmDelete = async () => {
        const res = await deleteMerchProduct(deleteModal.product.id);
        if (res.status === "success") {
            setDeleteModal({ isOpen: false, product: null });
            loadData();
        }
    };

    const filteredMerch = (Array.isArray(merch) ? merch : []).filter(item => {
        const nameMatch = (item.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        const catMatch = (item.category_name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        return nameMatch || catMatch;
    });

    return (
        <>
            <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Artist Merch
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage your apparel and branded accessories.
                        </p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> ADD MERCH ITEM
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search merch..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-[#222] rounded-xl border border-black/5">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`p-2 rounded-lg transition-all ${viewMode === "grid" ? "bg-white dark:bg-zinc-800 shadow-sm text-jazz-brown" : "text-black dark:text-white/40"}`}
                            title="Grid View"
                        >
                            <LayoutGrid size={18} />
                        </button>
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-2 rounded-lg transition-all ${viewMode === "list" ? "bg-white dark:bg-zinc-800 shadow-sm text-jazz-brown" : "text-black dark:text-white/40"}`}
                            title="List View"
                        >
                            <List size={18} />
                        </button>
                    </div>
                </div>

                {/* Merch Display */}
                {loading && merch.length === 0 ? (
                    <div className="py-40 flex flex-col items-center justify-center gap-4">
                        <Loader2 size={48} className="animate-spin text-jazz-brown" />
                        <p className="text-black dark:text-white/50 font-medium italic">Loading your merchandise...</p>
                    </div>
                ) : viewMode === "grid" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredMerch.map((item) => (
                            <div key={item.id} className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-black/5 transition-all">
                                <div className="aspect-square relative overflow-hidden bg-gray-100 dark:bg-zinc-800">
                                    <div className="absolute top-3 right-3 z-10 flex flex-col gap-2 items-end">
                                        <span className={`px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-sm ${item.status === 'active' ? "bg-green-500 text-white" : "bg-black text-white"}`}>
                                            {item.status === 'active' ? <Globe size={10} /> : <Lock size={10} />}
                                            {item.status}
                                        </span>
                                        {item.is_popular && (
                                            <span className="px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest bg-jazz-brown text-white shadow-sm flex items-center gap-1">
                                                <CheckCircle2 size={10} /> Popular
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-full h-full flex items-center justify-center">
                                        {item.image ? (
                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                        ) : (
                                            <ShoppingBag size={48} className="text-black/5" />
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleViewClick(item.id)}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleOpenModal(item)}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-75"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(item.id)}
                                            disabled={statusLoading[item.id]}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-100"
                                        >
                                            {statusLoading[item.id] ? <Loader2 size={18} className="animate-spin" /> : item.status === 'active' ? <Lock size={18} /> : <Globe size={18} />}
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(item)}
                                            className="p-3 bg-white rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-150"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-2 text-xs font-bold text-jazz-brown uppercase tracking-widest mb-2">
                                        <Tag size={12} /> {item.category_name}
                                    </div>
                                    <h3 className="font-bold text-black dark:text-white mb-4 line-clamp-1 h-6 leading-6 text-lg" style={{ fontFamily: "Playfair Display, serif" }}>
                                        {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/5">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-black dark:text-white/40 font-bold uppercase tracking-widest">Price</span>
                                            <span className="text-lg font-bold text-black dark:text-white flex items-center gap-0.5">
                                                <DollarSign size={14} className="text-jazz-brown" />{parseFloat(item.price).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className="text-xs text-black dark:text-white/40 font-bold uppercase tracking-widest">Stock</span>
                                            <span className={`text-sm font-bold flex items-center gap-1 ${item.stock_quantity === 0 ? "text-red-500" : item.stock_quantity < 10 ? "text-amber-500" : "text-black dark:text-white/60"}`}>
                                                <Layers size={14} /> {item.stock_quantity === 0 ? "OUT OF STOCK" : item.stock_quantity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 overflow-hidden shadow-sm">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-[#222] border-b border-black/5">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Product</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Category</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Price</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Stock</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {filteredMerch.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
                                                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <ShoppingBag size={20} className="text-black/10" />}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-sm text-black dark:text-white line-clamp-1">{item.name}</p>
                                                    <p className="text-xs text-jazz-brown font-bold uppercase tracking-tighter">ID: {item.id.split('-')[0]}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-black dark:text-white/60">{item.category_name}</td>
                                        <td className="px-6 py-4 font-bold text-sm text-black dark:text-white">${parseFloat(item.price).toFixed(2)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`text-sm font-bold ${item.stock_quantity === 0 ? "text-red-500" : item.stock_quantity < 10 ? "text-amber-500" : "text-black dark:text-white/60"}`}>
                                                {item.stock_quantity}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${item.status === 'active' ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"}`}>
                                                {item.status === 'active' ? <CheckCircle2 size={12} /> : <Clock size={12} />} {item.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <button onClick={() => handleViewClick(item.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"><Eye size={16} /></button>
                                                <button onClick={() => handleOpenModal(item)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"><Edit2 size={16} /></button>
                                                <button
                                                    onClick={() => handleToggleStatus(item.id)}
                                                    disabled={statusLoading[item.id]}
                                                    className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                >
                                                    {statusLoading[item.id] ? <Loader2 size={16} className="animate-spin" /> : item.status === 'active' ? <Lock size={16} /> : <Globe size={16} />}
                                                </button>
                                                <button onClick={() => handleDeleteClick(item)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-red-400 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && filteredMerch.length === 0 && (
                    <div className="py-20 text-center">
                        <ShoppingBag size={48} className="text-black/5 mx-auto mb-4" />
                        <p className="text-black dark:text-white/50 font-medium italic">No merchandise products found matching your search.</p>
                    </div>
                )}
            </div>

            {/* Add/Edit Product Modal */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-2xl rounded-3xl shadow-2xl border border-black/5 dark:border-white/5 overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-8 py-6 border-b border-black/5 flex items-center justify-between shrink-0">
                            <div>
                                <h2 className="text-2xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                                    {editingProduct ? "Edit Merch Item" : "Add Merch Item"}
                                </h2>
                                <p className="text-sm text-black dark:text-white/50 mt-1 uppercase tracking-widest font-bold">Product Details</p>
                            </div>
                            <button onClick={handleCloseModal} className="text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white p-2 bg-gray-100 dark:bg-zinc-800 rounded-full transition-all"><X size={20} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Item Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                            placeholder="e.g. Classic Jazz Tee"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Category Selection <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            required
                                            value={formData.category_id}
                                            onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                                            className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all appearance-none"
                                        >
                                            <option value="" disabled>Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                            {categories.length === 0 && <option value="" disabled>No Merch Categories Found</option>}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                                Price ($) <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-jazz-brown" size={18} />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    required
                                                    value={formData.price}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                                    className="w-full pl-12 pr-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                                Stock Qty <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-black dark:text-white/20" size={18} />
                                                <input
                                                    type="number"
                                                    required
                                                    value={formData.stock_quantity}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                                                    className="w-full pl-12 pr-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Product Image <span className="text-red-500">*</span>
                                        </label>
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            className="relative w-full aspect-video rounded-2xl border-2 border-dashed border-black/10 dark:border-white/10 hover:border-jazz-brown dark:hover:border-jazz-brown group cursor-pointer overflow-hidden bg-gray-50 dark:bg-[#222] transition-all flex flex-col items-center justify-center gap-2"
                                        >
                                            {imagePreview ? (
                                                <>
                                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <Plus className="text-white" size={32} />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-4 bg-white dark:bg-zinc-800 rounded-full shadow-sm text-black/20 group-hover:text-jazz-brown transition-colors">
                                                        <ImageIcon size={24} />
                                                    </div>
                                                    <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Click to upload JPG/PNG</p>
                                                </>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </div>

                                    <div className="flex items-center gap-6 pt-4">
                                        <div className="flex items-center gap-3">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={formData.is_popular}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jazz-brown"></div>
                                                <span className="ml-3 text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Mark as Popular</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all min-h-[120px] resize-none"
                                    placeholder="Enter details about this item (fabric, sizing, etc)..."
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading || isSubmitting}
                                className="w-full bg-black text-white py-5 rounded-2xl text-xs font-bold tracking-[0.2em] hover:bg-jazz-brown transition-all shadow-xl shadow-black/10 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading || isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                {editingProduct ? "UPDATE MERCH ITEM" : "SAVE MERCH ITEM"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteModal({ isOpen: false, product: null })} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-black/5 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                                <AlertCircle size={40} />
                            </div>
                            <h3 className="text-2xl font-normal text-black dark:text-white mb-3" style={{ fontFamily: "Playfair Display, serif" }}>Confirm Deletion</h3>
                            <p className="text-black dark:text-white/50 text-sm mb-8 leading-relaxed">
                                Are you sure you want to delete <span className="font-bold text-black">"{deleteModal.product.name}"</span>?
                                <br /><br />
                                <span className="text-red-500 font-bold uppercase tracking-wider text-xs">Warning:</span> This action is permanent and cannot be undone.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => setDeleteModal({ isOpen: false, product: null })}
                                    className="py-4 rounded-2xl border border-black/5 text-black dark:text-white/50 text-xs font-bold tracking-widest hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all"
                                >
                                    CANCEL
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={loading}
                                    className="bg-red-500 text-white py-4 rounded-2xl text-xs font-bold tracking-widest hover:bg-red-600 transition-all shadow-lg flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} DELETE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* View Product Modal */}
            <ViewArtistMerchModal
                itemId={viewProductId}
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setViewProductId(null);
                }}
            />
        </>
    );
}
